import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LabelWithInput from 'components/common/Input/LabelWithInput';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import 'assets/styles/common.scss';
import CheckValidation from 'utils/Validation';
import ToastNotification from 'components/common/Toast';
import { LocaleContext } from 'routes/Router';
import { FormattedMessage, useIntl } from 'react-intl';
import ReusableButton from 'components/common/Button';
import { resetPasswordCallAPI } from 'store/resetPassword/action';
import { useDispatch, useSelector } from 'react-redux';
import { getLandingURL } from 'routes/AppUrls';
import CircularProgress from '@mui/material/CircularProgress';
import AuthLayout from 'components/common/AuthLayout';
import Ptext from 'components/common/Ptext';
import loginHero from 'assets/images/login/loginHero.png';
import LoginLogo from 'assets/images/login/LogoAlt.svg';
import 'assets/styles/auth.scss';

const CreatePassword = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { locale } = useContext(LocaleContext);
  const location = useLocation();
  const [conf, setConf] = ToastNotification();
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [isConfirmPassVisible, setIsConfirmPassVisible] = useState(false);
  const [isError, setIsError] = useState({
    password: { error: false, errorMessage: '' },
    confirmPassword: { error: false, errorMessage: '' },
  });

  const params = new URLSearchParams(location.search);
  const ProviderToken = params.get('token');
  const createPasswordLoader = useSelector(
    (state) => state?.createPasswordReducer?.loading
  );

  const [formValue, setformValue] = useState({
    password: '',
    confirmPassword: '',
  });

  const handleSubmit = () => {
    const error = CheckValidation(formValue, intl);
    setIsError(error);

    if (error.submit) {
      handleCreatePassword();
    }
  };

  const handleCreatePassword = (data) => {
    let body = {
      password: formValue?.password,
      provider_id: data?.provider_id,
      token: ProviderToken,
    };
    dispatch(
      resetPasswordCallAPI(body, {
        run: true,
        toast: true,
        handleFunction: (res) => handleRedirect(res),
        handleToastError: (res) => handleError(res),
        navigate: navigate,
      })
    );
  };

  const handleError = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'error',
    });
  };

  const handleRedirect = (res) => {
    setConf({
      msg: res?.data?.message,
      variant: 'success',
    });
    setTimeout(() => {
      navigate(getLandingURL(locale));
    }, 1000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setformValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <AuthLayout
      heroImage={loginHero}
      logo={LoginLogo}
      quote="Allue transformed our salon by doubling our monthly bookings and completely eliminating no-shows"
      author="Alisa Lisa"
    >
      <div className="login-content">
        <Ptext variant="title" className="welcome-title">
          <FormattedMessage id="Reset Password" />
        </Ptext>

        <Ptext variant="body" className="welcome-subtitle mt-2">
          <FormattedMessage id="Create a new password for your account." />
        </Ptext>

        {/* PASSWORD */}
        <div className="mt-4">
          <LabelWithInput
            label={<FormattedMessage id="Password" />}
            placeholder={intl.formatMessage({
              id: 'Password',
            })}
            name="password"
            value={formValue?.password}
            type={isPassVisible ? 'text' : 'password'}
            onChange={handleInputChange}
            helperText={
              isError?.password?.error ? (
                ''
              ) : (
                <FormattedMessage id="* 8–16 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character" />
              )
            }
            InputProps={{
              endAdornment: !isPassVisible ? (
                <VisibilityOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    color: '#ABABAB',
                  }}
                  onClick={() => setIsPassVisible(true)}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    color: '#ABABAB',
                  }}
                  onClick={() => setIsPassVisible(false)}
                />
              ),
            }}
            errorText={
              isError &&
              isError?.password?.error &&
              isError?.password?.errorMessage
            }
          />
        </div>

        {/* CONFIRM PASSWORD */}
        <div className="mt-3">
          <LabelWithInput
            label={<FormattedMessage id="Confirm Password" />}
            placeholder={intl.formatMessage({
              id: 'Confirm Password',
            })}
            name="confirmPassword"
            value={formValue?.confirmPassword}
            type={isConfirmPassVisible ? 'text' : 'password'}
            onChange={handleInputChange}
            InputProps={{
              endAdornment: !isConfirmPassVisible ? (
                <VisibilityOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    color: '#ABABAB',
                  }}
                  onClick={() => setIsConfirmPassVisible(true)}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  sx={{
                    cursor: 'pointer',
                    color: '#ABABAB',
                  }}
                  onClick={() => setIsConfirmPassVisible(false)}
                />
              ),
            }}
            errorText={
              isError &&
              isError?.confirmPassword?.error &&
              isError?.confirmPassword?.errorMessage
            }
          />
        </div>

        {/* SUBMIT BUTTON */}
        <div className="mt-4">
          <ReusableButton
            fullWidth
            variant="contained"
            color="#FFFFFF"
            fontSize="16px"
            fontWeight="500"
            padding="14px"
            bgColor="#000000"
            borderRadius="999px"
            fontFamily="Poppins, sans-serif"
            endIcon={
              createPasswordLoader && (
                <CircularProgress
                  style={{
                    color: 'white',
                    width: '20px',
                    height: '20px',
                  }}
                />
              )
            }
            onClick={handleSubmit}
          >
            <FormattedMessage id="Reset Password" />
          </ReusableButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CreatePassword;
